/**
 * FikoSnapMotoru.ts — Profesyonel CAD snap/inference motoru (saf hesap)
 *
 * CATIA SmartPick / Onshape inference mantığı: ekran-uzayı (piksel) toleransı
 * ile en yüksek öncelikli snap noktasını seçer. Türler önceliğe göre:
 *   endpoint > center > midpoint > quadrant > yatay/dikey inference > nearest > grid
 * "Diskret" noktalar (uç/merkez) her zaman "sürekli" noktaları (nearest/grid) yener.
 *
 * Çıktı: snaplenmiş 2D düzlem noktası + tür (marker için) + inference çizgileri.
 * Render etmez — FikoSketchAraci marker/çizgi çizer.
 */

import * as THREE from 'three';
import { FikoSketch, ISketchNokta } from './FikoSketch';

export type SnapTip = 'endpoint' | 'center' | 'midpoint' | 'quadrant' | 'yatay' | 'dikey' | 'nearest' | 'grid' | 'serbest';

const ONCELIK: Record<SnapTip, number> = {
  endpoint: 1, center: 2, midpoint: 3, quadrant: 4, dikey: 5, yatay: 5, nearest: 8, grid: 9, serbest: 99,
};

const APERTURE_PX = 11;     // yakalama yarıçapı (piksel)
const INFERENCE_PX = 8;     // hizalama toleransı (piksel)

interface Aday { tip: SnapTip; p: ISketchNokta; }

export interface SnapSonuc {
  nokta: ISketchNokta;                               // snaplenmiş düzlem noktası
  tip: SnapTip;
  ekran: { x: number; y: number };                   // marker ekran konumu (px)
  inference: { a: ISketchNokta; b: ISketchNokta }[]; // kesikli hizalama çizgileri (düzlem)
}

const _w = new THREE.Vector3();

export class FikoSnapMotoru {
  aktif = true;

  /** Ana çözüm: ham düzlem noktası + fare ekran konumu → en iyi snap. */
  coz(
    sketch: FikoSketch,
    hamDuzlem: ISketchNokta,
    fareEkran: { x: number; y: number },
    kamera: THREE.Camera,
    rect: DOMRect,
    gridAdim: number,
  ): SnapSonuc {
    if (!this.aktif) {
      return { nokta: hamDuzlem, tip: 'serbest', ekran: fareEkran, inference: [] };
    }

    const adaylar = this.adaylariTopla(sketch);
    const ekranla = (p: ISketchNokta) => {
      sketch.duzlem2Dunya(p, _w).project(kamera);
      return { x: (_w.x * 0.5 + 0.5) * rect.width, y: (-_w.y * 0.5 + 0.5) * rect.height };
    };

    // 1) Diskret nokta snap'leri (aperture içinde, önceliğe + piksel mesafeye göre)
    let enIyi: { aday: Aday; d: number; ekran: { x: number; y: number } } | null = null;
    for (const a of adaylar) {
      const s = ekranla(a.p);
      const d = Math.hypot(s.x - fareEkran.x, s.y - fareEkran.y);
      if (d > APERTURE_PX) continue;
      if (!enIyi || ONCELIK[a.tip] < ONCELIK[enIyi.aday.tip] ||
        (ONCELIK[a.tip] === ONCELIK[enIyi.aday.tip] && d < enIyi.d)) {
        enIyi = { aday: a, d, ekran: s };
      }
    }
    if (enIyi) return { nokta: enIyi.aday.p, tip: enIyi.aday.tip, ekran: enIyi.ekran, inference: [] };

    // 2) Yatay/Dikey inference (uç/merkez noktalarıyla hizalanma)
    const inf = this.inferenceCoz(adaylar, hamDuzlem, fareEkran, ekranla);
    if (inf) return inf;

    // 3) Eleman üzeri en yakın nokta (nearest)
    const near = this.nearestCoz(sketch, hamDuzlem, fareEkran, ekranla);
    if (near) return near;

    // 4) Grid
    if (gridAdim > 0) {
      const p = { x: Math.round(hamDuzlem.x / gridAdim) * gridAdim, y: Math.round(hamDuzlem.y / gridAdim) * gridAdim };
      return { nokta: p, tip: 'grid', ekran: ekranla(p), inference: [] };
    }
    return { nokta: hamDuzlem, tip: 'serbest', ekran: fareEkran, inference: [] };
  }

  /** Sketch elemanlarından tür-etiketli aday noktalar (düzlem koordinatı). */
  private adaylariTopla(sketch: FikoSketch): Aday[] {
    const out: Aday[] = [];
    for (const el of sketch.elemanlar) {
      if (el.tip === 'cizgi') {
        const [a, b] = el.noktalar;
        out.push({ tip: 'endpoint', p: a }, { tip: 'endpoint', p: b });
        out.push({ tip: 'midpoint', p: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } });
      } else if (el.tip === 'dikdortgen') {
        const [a, b] = el.noktalar;
        const k = [{ x: a.x, y: a.y }, { x: b.x, y: a.y }, { x: b.x, y: b.y }, { x: a.x, y: b.y }];
        for (const c of k) out.push({ tip: 'endpoint', p: c });
        out.push({ tip: 'center', p: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } });
        for (let i = 0; i < 4; i++) { const c = k[i], n = k[(i + 1) % 4]; out.push({ tip: 'midpoint', p: { x: (c.x + n.x) / 2, y: (c.y + n.y) / 2 } }); }
      } else if (el.tip === 'cember') {
        const m = el.noktalar[0]; const r = el.r || 0;
        out.push({ tip: 'center', p: m });
        out.push({ tip: 'quadrant', p: { x: m.x + r, y: m.y } }, { tip: 'quadrant', p: { x: m.x - r, y: m.y } },
          { tip: 'quadrant', p: { x: m.x, y: m.y + r } }, { tip: 'quadrant', p: { x: m.x, y: m.y - r } });
      } else if (el.tip === 'nokta') {
        out.push({ tip: 'endpoint', p: el.noktalar[0] });
      }
    }
    return out;
  }

  private inferenceCoz(
    adaylar: Aday[], ham: ISketchNokta, fareEkran: { x: number; y: number },
    ekranla: (p: ISketchNokta) => { x: number; y: number },
  ): SnapSonuc | null {
    let dikeyKaynak: ISketchNokta | null = null;   // aynı x (dikey çizgi)
    let yatayKaynak: ISketchNokta | null = null;   // aynı y (yatay çizgi)
    let dikeyD = INFERENCE_PX, yatayD = INFERENCE_PX;
    for (const a of adaylar) {
      if (a.tip === 'midpoint' && (dikeyKaynak || yatayKaynak)) continue;
      const s = ekranla(a.p);
      const dx = Math.abs(s.x - fareEkran.x), dy = Math.abs(s.y - fareEkran.y);
      if (dx < dikeyD) { dikeyD = dx; dikeyKaynak = a.p; }
      if (dy < yatayD) { yatayD = dy; yatayKaynak = a.p; }
    }
    if (!dikeyKaynak && !yatayKaynak) return null;
    const nokta: ISketchNokta = { x: dikeyKaynak ? dikeyKaynak.x : ham.x, y: yatayKaynak ? yatayKaynak.y : ham.y };
    const inference: { a: ISketchNokta; b: ISketchNokta }[] = [];
    if (dikeyKaynak) inference.push({ a: dikeyKaynak, b: nokta });
    if (yatayKaynak) inference.push({ a: yatayKaynak, b: nokta });
    return { nokta, tip: dikeyKaynak && yatayKaynak ? 'endpoint' : (dikeyKaynak ? 'dikey' : 'yatay'), ekran: ekranla(nokta), inference };
  }

  private nearestCoz(
    sketch: FikoSketch, ham: ISketchNokta, fareEkran: { x: number; y: number },
    ekranla: (p: ISketchNokta) => { x: number; y: number },
  ): SnapSonuc | null {
    let enIyi: { p: ISketchNokta; d: number } | null = null;
    for (const el of sketch.elemanlar) {
      let p: ISketchNokta | null = null;
      if (el.tip === 'cizgi') p = this.cizgiUzeriEnYakin(el.noktalar[0], el.noktalar[1], ham);
      else if (el.tip === 'cember') { const m = el.noktalar[0]; const r = el.r || 0; const a = Math.atan2(ham.y - m.y, ham.x - m.x); p = { x: m.x + Math.cos(a) * r, y: m.y + Math.sin(a) * r }; }
      if (!p) continue;
      const s = ekranla(p);
      const d = Math.hypot(s.x - fareEkran.x, s.y - fareEkran.y);
      if (d < APERTURE_PX && (!enIyi || d < enIyi.d)) enIyi = { p, d };
    }
    return enIyi ? { nokta: enIyi.p, tip: 'nearest', ekran: ekranla(enIyi.p), inference: [] } : null;
  }

  private cizgiUzeriEnYakin(a: ISketchNokta, b: ISketchNokta, p: ISketchNokta): ISketchNokta {
    const dx = b.x - a.x, dy = b.y - a.y;
    const uzun2 = dx * dx + dy * dy;
    if (uzun2 < 1e-9) return { ...a };
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / uzun2;
    t = Math.max(0, Math.min(1, t));
    return { x: a.x + dx * t, y: a.y + dy * t };
  }

  /** Marker sembolü (tür → karakter). */
  static sembol(tip: SnapTip): string {
    switch (tip) {
      case 'endpoint': return '□';
      case 'midpoint': return '△';
      case 'center': return '○';
      case 'quadrant': return '◇';
      case 'nearest': return '⋈';
      case 'yatay': case 'dikey': return '⟂';
      default: return '';
    }
  }
}
